import { Flash, Box, Column, Grid, TextInput, Section, Select, Text } from '@acast/decibel';
import { resolve } from 'acast-www-utils';
import updateEntity from 'app/entity/actions/update';
import T from 'prop-types';
import React, { Component } from 'react';
import { Episode, MediaFile } from 'resources';
import Field from 'shared/components/field';
import RichTextEditor from 'shared/components/rich-text-editor';
import TokenInput from 'shared/components/token-input';
import replaceEpisodeAudioFile from 'shared/helpers/replace-episode-audio-file';
import resourceProp from 'shared/helpers/resource-prop';
import { markerPropType } from '../../../customPropTypes';
import Media from './media';

class BasicInfo extends Component {
  static propTypes = {
    episode: resourceProp(Episode),
    markers: T.arrayOf(markerPropType),
    mediaFile: resourceProp(MediaFile),
    retryEncoder: T.func,
    onAudioFileRejected: T.func,
    onAudioFileReceived: T.func,
  };

  state = {
    didFileGetRejected: false,
  };

  onAudioFileRejected = () => {
    this.setState({ didFileGetRejected: true });
  };

  onAudioFileReceived = file => {
    this.setState({ didFileGetRejected: false });
    const { episode, markers } = this.props;
    replaceEpisodeAudioFile(episode.data.id, episode.data.channel, file, markers);
  };

  getAudioRejectedError() {
    if (this.state.didFileGetRejected) {
      return (
        <Flash intent="danger">
          Unsupported file type. We only support .mp3 files currently. Sorry for the inconvenience.
        </Flash>
      );
    }
    return null;
  }

  getEpisodeProperty(key, defaultValue) {
    const { episode } = this.props;
    return resolve(episode, `data.${key}`, defaultValue) || defaultValue;
  }

  setEpisodeProperty(key, deserialize = value => value) {
    const { episode } = this.props;
    return value => {
      const deserializedValue = deserialize(value);
      const model = episode.data;
      if (model[key] !== deserializedValue) {
        updateEntity(Episode, model.merge({ [key]: deserializedValue }));
      }
    };
  }

  render() {
    const audioAndCoverImageField = (
      <Box mb={4} flexDirection="column">
        <Media
          episode={this.props.episode}
          markers={this.props.markers}
          mediaFile={this.props.mediaFile}
          onFileReceived={this.onAudioFileReceived}
          onFileRejected={this.onAudioFileRejected}
          retryEncoder={this.props.retryEncoder}
        />
      </Box>
    );

    const seasonNumberField = (
      <Field
        name="season-number"
        label="Season Number"
        help={
          <>
            The option to group episodes by season.
            <br />
            <br />
            Specify which season the episode should be included in. For shows with only one season,
            Apple Podcasts won't display a season number.
          </>
        }
      >
        <TextInput
          value={String(this.getEpisodeProperty('seasonNumber', ''))}
          placeholder="..."
          type="number"
          name="season-number"
          onChange={this.setEpisodeProperty('seasonNumber', event =>
            parseInt(event.target.value, 10)
          )}
        />
      </Field>
    );

    const episodeTypeField = (
      <Field
        name="episode-type"
        label="Episode Type"
        help={
          <>
            <b>Full:</b> When you are submitting a full episode or piece of content for a show. This
            will be included in the main show feed.
            <br />
            <br />
            <b>Trailer:</b> For short, promotional pieces of content such as show previews. This
            will be included in a separate "Trailer" section on Apple Podcasts.
            <br />
            <br />
            <b>Bonus:</b> Extra show content, such as behind-the-scenes or cast interviews. This
            will appear at the end of a season.
          </>
        }
      >
        <Select
          value={this.getEpisodeProperty('episodeType', 'full')}
          onChange={this.setEpisodeProperty('episodeType', event => event.target.value)}
        >
          <Select.Option value="full">Full (default)</Select.Option>
          <Select.Option value="trailer">Trailer</Select.Option>
          <Select.Option value="bonus">Bonus</Select.Option>
        </Select>
      </Field>
    );

    const episodeNumberField = (
      <Field
        name="episode-number"
        label="Episode Number"
        help="The option to specify the order of episodes within a season, so they do not appear chronologically."
      >
        <TextInput
          value={String(this.getEpisodeProperty('episodeNumber', ''))}
          placeholder="..."
          type="number"
          name="episode-number"
          onChange={this.setEpisodeProperty('episodeNumber', event =>
            parseInt(event.target.value, 10)
          )}
        />
      </Field>
    );

    const nameField = (
      <Field name="name" label="Name" isRequired>
        <TextInput
          value={this.getEpisodeProperty('name', '')}
          placeholder="..."
          name="name"
          onChange={this.setEpisodeProperty('name', event => event.target.value)}
        />
      </Field>
    );

    const descriptionField = (
      <Field
        name="description"
        label="Description"
        help="Description text formatting will appear in certain Apple Podcasts views, the Acast app and web player and any podcatcher that supports HTML."
        isRequired
      >
        <RichTextEditor
          value={this.getEpisodeProperty('description', '')}
          placeholder="..."
          onChange={this.setEpisodeProperty('description')}
        />
      </Field>
    );

    const tagField = (
      <Field
        label="Tags"
        help="These are used by the search function within the Acast apps and web player."
      >
        <TokenInput
          tokens={this.getEpisodeProperty('tags', [])}
          onChange={this.setEpisodeProperty('tags')}
          placeholder="Add tags"
        />
      </Field>
    );

    const explicitField = (
      <Field
        label="Explicit"
        help="Select if your show contains any bad language or content that might be inappropriate for children. This will tag the individual episode as 'Explicit'."
      >
        <Select
          value={this.getEpisodeProperty('rating', 'no')}
          onChange={this.setEpisodeProperty('rating', event => event.target.value)}
        >
          <Select.Option value="no">No</Select.Option>
          <Select.Option value="yes">Yes</Select.Option>
        </Select>
      </Field>
    );

    return (
      <Section>
        <Text variant="h3">Basic Info</Text>
        <Grid>
          <Column>{audioAndCoverImageField}</Column>
          <Column width="50%">{seasonNumberField}</Column>
          <Column width="50%">{episodeTypeField}</Column>
          <Column width="25%">{episodeNumberField}</Column>
          <Column width="75%">{nameField}</Column>
          <Column>{descriptionField}</Column>
          <Column width="50%">{tagField}</Column>
          <Column width="50%">{explicitField}</Column>
        </Grid>
      </Section>
    );
  }
}

export default BasicInfo;
